import React, { useState } from "react";
import { BellOffIcon, CheckCheckIcon, SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { useNotifications } from "../../contexts/NotificationsContext";
import { PageHeader } from "../../components/app/PageHeader";
import { Segmented } from "../../components/ui/Segmented";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { NotificationItem } from "../../components/notifications/NotificationItem";
import type { NotificationDay, NotificationKind } from "../../types/notifications";
type Filter = "all" | "unread" | "learning" | "account";
const learningKinds: NotificationKind[] = ["streak", "review", "translator", "achievement", "course"];
const days: NotificationDay[] = ["Today", "Yesterday", "Earlier this week"];
export function Notifications() {
  const {
    items,
    unreadCount,
    markRead,
    toggleRead,
    markAllRead,
    dismiss,
    restore
  } = useNotifications();
  const [filter, setFilter] = useState<Filter>("all");
  const filtered = items.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "learning") return learningKinds.includes(n.kind);
    if (filter === "account") return n.kind === "account";
    return true;
  });
  const onDismiss = (id: string) => {
    const removed = dismiss(id);
    if (removed) toast("Notification dismissed", {
      action: {
        label: "Undo",
        onClick: () => restore(removed)
      }
    });
  };
  return <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10" data-id="element-1379" data-mp="1q62ltomlvmr9">
      <PageHeader title="Notifications" description="Reminders, reviews and news from your courses. Tune what you get in Settings." actions={<>
            <BrutalButton variant="surface" size="sm" disabled={unreadCount === 0} onClick={() => {
        markAllRead();
        toast("All caught up");
      }} data-id="element-1381" data-mp="246lt2tj8yosd">
              <CheckCheckIcon className="h-4 w-4" data-id="element-1382" data-mp="9t489ea88611" /> Mark all read
            </BrutalButton>
            <BrutalButton variant="surface" size="sm" to="/app/profile/settings#notifications" ariaLabel="Notification settings" data-id="element-1383" data-mp="1sesdgwxy0ay">
              <SettingsIcon className="h-4 w-4" data-id="element-1384" data-mp="3ty1ufs3e8uns" /> Settings
            </BrutalButton>
          </>} data-id="element-1380" data-mp="1u7whfihy20kk" />

      <div className="no-scrollbar -mx-4 mt-6 overflow-x-auto px-4" data-id="element-1385" data-mp="6qziuome55m7">
        <Segmented id="notif-filter" label="Filter notifications" value={filter} onChange={setFilter} options={[{
        value: "all",
        label: "All"
      }, {
        value: "unread",
        label: unreadCount ? `Unread ${unreadCount}` : "Unread"
      }, {
        value: "learning",
        label: "Learning"
      }, {
        value: "account",
        label: "Account"
      }]} data-id="element-1386" data-mp="6pohevgf49y9" />
      </div>

      {filtered.length === 0 ? <div className="mt-6 flex flex-col items-center rounded-card border-3 border-dashed border-line/40 px-6 py-16 text-center" data-id="element-1387" data-mp="14fjcevmve8l4">
          <BellOffIcon className="h-8 w-8 text-ink-2" aria-hidden="true" data-id="element-1388" data-mp="zdkalq6s3jqa" />
          <p className="mt-3 text-lg font-bold text-ink" data-id="element-1389" data-mp="1j1yjz4cio4j6">{filter === "unread" ? "You're all caught up" : "Nothing here yet"}</p>
          <p className="mt-1 max-w-sm text-[15px] text-ink-2" data-id="element-1390" data-mp="1n0pjtj8da09z">
            {filter === "unread" ? "Every notification has been read." : "When there's news for this category, it'll appear here."}
          </p>
        </div> : <div className="mt-6 space-y-8" data-id="element-1391" data-mp="zcxunf1q9sra">
          {days.map(day => {
        const group = filtered.filter(n => n.day === day);
        if (group.length === 0) return null;
        return <section key={day} aria-labelledby={`day-${day}`} data-id="element-1392" data-mp="qdkmqqt95c8o">
                <h2 id={`day-${day}`} className="mb-2 px-1 text-sm font-bold text-ink-2" data-id="element-1393" data-mp="vbvw70fmxkqd">
                  {day}
                </h2>
                <ul className="space-y-1 rounded-card border-3 border-line bg-surface p-2 shadow-brutal-sm" data-id="element-1394" data-mp="gfmw5wnx73cw">
                  {group.map(n => <NotificationItem key={n.id} item={n} onOpen={item => markRead(item.id)} onToggleRead={toggleRead} onDismiss={onDismiss} data-id="element-1395" data-mp="lfj2w3o058w5" />)}
                </ul>
              </section>;
      })}
        </div>}
    </div>;
}